import type { Control, FieldValues, Path, UseFormSetValue, UseFormGetValues } from "react-hook-form";
import { useWatch } from "react-hook-form";
import { AddressInput } from "./AddressInput";
import type { AddressData, AddressInputProps } from "./AddressInput.types";

export interface AddressInputControllerProps<TFieldValues extends FieldValues = FieldValues>
  extends Omit<AddressInputProps, "value" | "onChange"> {
  name: Path<TFieldValues>;
  control: Control<TFieldValues>;
  setValue?: UseFormSetValue<TFieldValues>;
  getValues?: UseFormGetValues<TFieldValues>;
  onChangeAddress?: (data: AddressData) => void;
}

export function AddressInputController<TFieldValues extends FieldValues = FieldValues>({
  name,
  control,
  setValue,
  getValues,
  onChangeAddress,
  autocomplete = true,
  ...addressInputProps
}: AddressInputControllerProps<TFieldValues>) {
  const watchedValues = useWatch({ control });

  const streetAddress =
    (watchedValues as any)?.[name] ??
    (watchedValues as any)?.street_address ??
    getValues?.(name) ??
    getValues?.("street_address" as Path<TFieldValues>) ??
    "";
  const city =
    (watchedValues as any)?.city ??
    getValues?.("city" as Path<TFieldValues>) ??
    "";
  const postalCode =
    (watchedValues as any)?.postal_code ??
    getValues?.("postal_code" as Path<TFieldValues>) ??
    "";
  const stateOrRegion =
    (watchedValues as any)?.state_or_region ??
    getValues?.("state_or_region" as Path<TFieldValues>) ??
    "";
  const country =
    (watchedValues as any)?.country ??
    getValues?.("country" as Path<TFieldValues>) ??
    "";
  const latitude =
    (watchedValues as any)?.latitude ??
    getValues?.("latitude" as Path<TFieldValues>) ??
    null;
  const longitude =
    (watchedValues as any)?.longitude ??
    getValues?.("longitude" as Path<TFieldValues>) ??
    null;

  const currentAddressValue: AddressData = {
    street_address: streetAddress,
    address_line_1: streetAddress,
    city,
    postal_code: postalCode,
    state_or_region: stateOrRegion,
    country,
    latitude,
    longitude,
    is_manual_entry: !autocomplete,
  };

  const handleAddressChange = (data: AddressData) => {
    if (setValue) {
      const currentValues = (watchedValues as any) || getValues?.() || {};
      const fieldsToCheck: (keyof AddressData)[] = [
        "street_address",
        "city",
        "state_or_region",
        "postal_code",
        "country",
        "latitude",
        "longitude",
      ];

      for (const field of fieldsToCheck) {
        const val = data[field];
        if (val !== undefined && currentValues[field] !== val) {
          setValue(field as Path<TFieldValues>, val as any, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
      }

      if (name && data.street_address !== undefined && currentValues[name] !== data.street_address) {
        setValue(name, data.street_address as any, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }
    if (onChangeAddress) {
      onChangeAddress(data);
    }
  };

  return (
    <AddressInput
      {...addressInputProps}
      autocomplete={autocomplete}
      value={currentAddressValue}
      onChange={handleAddressChange}
    />
  );
}
